/**
 * The World Map, drawn (lane AK, plan 22): a parchment sheet with each place as
 * a tome-style marker and each road as a dotted line (SVG stroke-dasharray).
 * The main road is gilt and heavier; side paths are fine ink dots. The current
 * place carries a "you are here" ring. Hover or focus a place for its name and
 * level band. Static DOM/SVG: it re-renders on hover and on map change only,
 * never per frame, and draws nothing on the game canvas.
 *
 * Presentational: takes a built model (worldMapModel.ts), so the window and the
 * offline gallery render the same markup.
 */
import React, { useLayoutEffect, useRef, useState } from 'react';
import { hudTomeTokens } from '../../HUD/useHudTomeVibe';
import { zoneTomeVibe } from '../../primitives/tome/zoneTomeVibes';
import { resolveMapPresentationId, type WorldMapModel, type WorldMapPlace, type WorldMapRoad } from './worldMapModel';
import { labelPosition, roadPath } from './worldMapGeometry';
import type { WorldMapKnownPlaces } from './worldMapKnownPlaces';
import { WorldMapKnownPlaceNotes } from './WorldMapKnownPlaceNotes';
import './WorldMap.css';

const Marker: React.FC<{ place: WorldMapPlace }> = ({ place }) => {
  const r = place.onMainRoute ? 9 : 7;
  if (place.zoneType === 'town') {
    return <rect className="world-map-marker-shape" x={place.x - r} y={place.y - r} width={r * 2} height={r * 2} rx={2.5}
      transform={`rotate(45 ${place.x} ${place.y})`} />;
  }
  return <>
    <circle className="world-map-marker-shape" cx={place.x} cy={place.y} r={r} />
    {place.zoneType === 'dungeon' && <circle className="world-map-marker-core" cx={place.x} cy={place.y} r={r * 0.45} />}
  </>;
};

const Road: React.FC<{ road: WorldMapRoad; from: WorldMapPlace; to: WorldMapPlace; lit: boolean }> = ({ road, from, to, lit }) =>
  <path className="world-map-road" d={roadPath(from, to, road.key, road.via)} data-road={road.key}
    data-road-kind={road.via ? 'ferry' : road.main ? 'main' : 'side'} data-road-lit={lit || undefined}
    data-road-sealed={road.sealed || undefined}
    markerEnd={road.oneWay ? 'url(#world-map-one-way)' : undefined}>
    {road.sealed && <title>{road.oneWay ? `The way back to ${from.name} is sealed` : 'This way is sealed'}</title>}
    {road.via && !road.sealed && <title>{`Ferry: ${from.name} – ${to.name}`}</title>}
  </path>;

/** Soft watercolour land under the places (plan 28 D54): radial washes that fade to nothing at
 *  their rims (no coastline stroke, no blur filter: a filtered sheet this size seams in Chrome's tiles). */
const Lands: React.FC<{ model: WorldMapModel }> = ({ model }) => <g className="world-map-lands" aria-hidden="true">
  <rect className="world-map-sea" x={0} y={0} width={model.view.width} height={model.view.height} fill="url(#world-map-sea)" />
  {model.regions.map(region => <g key={region.id} className="world-map-land" data-region={region.id}>
    {region.washes.map((wash, index) => <ellipse key={index} cx={wash.cx} cy={wash.cy} rx={wash.rx} ry={wash.ry} fill="url(#world-map-wash)" />)}
  </g>)}
  {model.regions.filter(region => region.label).map(region =>
    <text key={region.id} className="world-map-region-label" data-region-label={region.id}
      x={region.label!.x} y={region.label!.y} textAnchor="middle">{region.label!.text}</text>)}
</g>;

export interface WorldMapViewProps {
  model: WorldMapModel;
  /** The map the player is on; null or an off-map id shows no "you are here". */
  currentMapId: string | null;
  /** This character's discovered pocket places by parent id (worldMapKnownPlaces.ts); none when omitted. */
  knownPlaces?: WorldMapKnownPlaces;
}

export const WorldMapView: React.FC<WorldMapViewProps> = ({ model, currentMapId, knownPlaces }) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const presentationMapId = resolveMapPresentationId(currentMapId) ?? null;
  const byId = new Map(model.places.map(place => [place.id, place]));
  const current = presentationMapId ? byId.get(presentationMapId) ?? null : null;
  const shown = (hoveredId ? byId.get(hoveredId) : null) ?? current;
  const tokens = hudTomeTokens(zoneTomeVibe(current?.id));
  const { width, height } = model.view;
  const sheetRef = useRef<HTMLDivElement>(null);

  // A narrow window pans the map (WorldMap.css); centre the current place once per map change.
  const currentX = current?.x ?? null;
  const currentY = current?.y ?? null;
  useLayoutEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet || currentX === null || currentY === null) return;
    if (sheet.scrollWidth > sheet.clientWidth) sheet.scrollLeft = (currentX / width) * sheet.scrollWidth - sheet.clientWidth / 2;
    if (sheet.scrollHeight > sheet.clientHeight) sheet.scrollTop = (currentY / height) * sheet.scrollHeight - sheet.clientHeight / 2;
  }, [currentX, currentY, width, height]);

  return <div className={`world-map ${tokens.className}`} style={tokens.style} data-world-map-current={current?.id ?? ''}>
    <header className="world-map-heading">
      <span className="world-map-kicker">World map</span>
      <h1>{model.title}</h1>
    </header>
    <div className="world-map-sheet" ref={sheetRef}>
      <svg className="world-map-svg" style={{ aspectRatio: `${width} / ${height}` }} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet"
        role="img" aria-label={`${model.title} world map${current ? `, you are in ${current.name}` : ''}`}>
        <defs>
          <radialGradient id="world-map-sea">
            <stop offset="0.7" className="world-map-sea-open" />
            <stop offset="1" className="world-map-sea-edge" />
          </radialGradient>
          <radialGradient id="world-map-wash">
            <stop offset="0" className="world-map-wash-core" />
            <stop offset="0.72" className="world-map-wash-body" />
            <stop offset="1" className="world-map-wash-rim" />
          </radialGradient>
          <marker id="world-map-one-way" viewBox="0 0 10 10" refX="16" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M 0 1 L 9 5 L 0 9 z" className="world-map-arrow" />
          </marker>
        </defs>
        <Lands model={model} />
        <g className="world-map-compass" transform="translate(62 78)" aria-hidden="true">
          <circle r="26" />
          <path d="M 0 -30 L 6 0 L 0 30 L -6 0 Z" />
          <text y="-36" textAnchor="middle">N</text>
        </g>
        <g className="world-map-roads">
          {model.roads.map(road => {
            const from = byId.get(road.from);
            const to = byId.get(road.to);
            if (!from || !to) return null;
            const lit = !!hoveredId && (road.from === hoveredId || road.to === hoveredId);
            return <Road key={road.key} road={road} from={from} to={to} lit={lit} />;
          })}
        </g>
        <g className="world-map-places">
          {model.places.map(place => {
            const label = labelPosition(place);
            const isCurrent = place.id === current?.id;
            return <g key={place.id} className="world-map-place" data-place={place.id} data-place-type={place.zoneType ?? 'unknown'}
              data-place-route={place.onMainRoute ? 'main' : 'side'} data-place-current={isCurrent || undefined}
              data-place-hovered={place.id === hoveredId || undefined}
              tabIndex={0} role="button" aria-label={`${place.name}${place.band ? `, ${place.band}` : ''}${isCurrent ? ', you are here' : ''}`}
              onPointerEnter={() => setHoveredId(place.id)} onPointerLeave={() => setHoveredId(null)}
              onFocus={() => setHoveredId(place.id)} onBlur={() => setHoveredId(null)}>
              <title>{place.band ? `${place.name} · ${place.band}` : place.name}</title>
              <circle className="world-map-hit" cx={place.x} cy={place.y} r={22} />
              {isCurrent && <circle className="world-map-here-ring" cx={place.x} cy={place.y} r={17} />}
              <Marker place={place} />
              <text className="world-map-label" x={label.x} y={label.y} textAnchor={label.anchor}>{place.name}</text>
            </g>;
          })}
        </g>
      </svg>
    </div>
    <footer className="world-map-strip" aria-live="polite">
      {shown ? <>
        <strong className="world-map-strip-name">{shown.name}</strong>
        {shown.band && <span className="world-map-strip-band">{shown.band}</span>}
        <span className="world-map-strip-route">{shown.onMainRoute ? 'On the main road' : 'Side path'}</span>
        {shown.id === current?.id && <span className="world-map-strip-here">You are here</span>}
        <WorldMapKnownPlaceNotes places={knownPlaces?.[shown.id]} currentMapId={currentMapId} />
      </> : <span className="world-map-strip-hint">You are off these roads. Hover a place to see its name and levels.</span>}
      <span className="world-map-legend" aria-hidden="true">
        <svg viewBox="0 0 40 8" width="40" height="8"><path className="world-map-road" data-road-kind="main" d="M 2 4 L 38 4" /></svg> Main road
        <svg viewBox="0 0 40 8" width="40" height="8"><path className="world-map-road" data-road-kind="side" d="M 2 4 L 38 4" /></svg> Side path
        <svg viewBox="0 0 40 8" width="40" height="8"><path className="world-map-road" data-road-kind="ferry" d="M 2 4 L 38 4" /></svg> Ferry
      </span>
    </footer>
  </div>;
};
